import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from "clsx";
import {connect} from "react-redux";
import {compose} from "recompose";
import {bindActionCreators} from "redux";
import ActionCreator from './../store/actions/index';
import {Button, withStyles} from "@material-ui/core";
import {withRouter} from "react-router-dom";
import DriversTable from '../components/DriverComponents/DriversTable';
import AddDriver from './../components/DriverComponents/AddDriver/AddDriver';
import t from "../languages/pl";
import AddDriversTemplate from "../templates/AddDriversTemplate.csv";
import {countHeightWhen2HeightToolbarUsed, isEmail, isPhoneNumber, isRfid, notArrayOrEmpty} from '../utils/helpers';
import NotificationDialog from '../components/DriverComponents/AddDriver/NotificationDialog';
import withSubordinateUser from '../components/HOC/withSubordinateUser';
import EcoChartDialog from '../components/Utilities/EcoChart/EcoChartDialog';
import SearchField from '../components/Common/InputFields/SearchField';
import ImportOrExportFileWithTemplate from '../components/Common/ImportOrExportFileWithTemplate';
import PaperContainer from '../components/Common/PaperContainer';
import {useWindowSize} from '../utils/hooks/useWindowResize';

const useStyles = theme => ({
    paper: {
        margin: theme.spacing(1),
        padding: theme.spacing(2)
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginBottom: theme.spacing(1)
    },
    buttonGroup: {
        marginRight: theme.spacing(1)
    },
    button: {
        margin: theme.spacing(1),
        textDecoration: "none"
    },
    hide: {
        display: "none"
    },
    autocomplete: {
        marginRight: "auto",
        marginLeft: theme.spacing(1),
    }
});

const checkRfidValid = (drivers, id, rfid) => {
    let result = drivers.find(driver => driver.kierowca_rfid === rfid && driver.kierowca_id !== id);
    return !result;
};

const checkEmailValid = (driver) => {
    return !!(driver.kierowca_email === "" || isEmail(driver.kierowca_email));
};

const checkDataComplete = data => {
    if (data.kierowca_nazwa.length < 0) {
        return false;
    }
    return isRfid(data.kierowca_rfid);

};

const mapDriversFromFile = (driver, groups) => {
    let driverGroup = groups.find(group => group.name === driver["Grupa"]);
    return {
        kierowca_nazwa: driver.Kierowca,
        kierowca_telefon: driver.Telefon,
        kierowca_email: driver.Email,
        kierowca_opis_rejon: driver["Rodzaj jazdy rejon"],
        kierowca_opis_poza_rejon: driver["Rodzaj jazdy poza rejonem"],
        kierowca_rfid: driver.RFID,
        kierowca_grupa: driverGroup ? driverGroup.id : ''
    };
};

const csvHeaders = [
    {label: "Kierowca", key: "kierowca_nazwa"},
    {label: "Telefon", key: "kierowca_telefon"},
    {label: "Email", key: "kierowca_email"},
    {label: "Rodzaj jazdy rejon", key: "kierowca_opis_rejon"},
    {label: "Rodzaj jazdy poza rejonem", key: "kierowca_opis_poza_rejon"},
    {label: "Grupa", key: "kierowca_grupa.nazwa"},
    {label: "RFID", key: "kierowca_rfid"},
    {label: "Klucz do aplikacji", key: 'kierowca_klucz_mobilny'},
    {label: "Kilometry ECO", key: 'kierowca_km'}
];

const DriversView = props => {
    const {classes, groups, drivers, nonAdminUser, chartData} = props;
    const [open, setOpen] = useState(false);
    const [driverIdToEdit, setDriverIdToEdit] = useState(null);
    const [notificationDialog, setNotificationDialog] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);
    const [searchDriver, setSearchDriver] = useState(null);
    const {height} = useWindowSize();

    useEffect(() => {
        props.getDrivers();
    }, []);

    const handleDriverSubmit = (id, driver) => {
        if (!checkDataComplete(driver))
            return props.dataNotValid();
        if (!checkRfidValid(drivers, id, driver.kierowca_rfid))
            return props.rfidNotValid();
        if (!checkEmailValid(driver))
            return props.emailNotValid();
        if (id)
            props.patchDriver(id, driver);
        else props.addDriver(driver);
        handleDialogClose();
    };

    const handleCsvLoaded = data => {
        if (notArrayOrEmpty(data))
            return;
        let incompleteData = [];
        let duplicatedNameOrRFID = [];
        let withoutValidEmail = [];
        let withoutValidPhone = [];
        let newDrivers = [];
        data.forEach(driverDtin => {
            let driver = mapDriversFromFile(driverDtin, groups);
            if (checkDataComplete(driver)) {
                let notValidNameOrRFID = Boolean(
                    [...drivers, ...newDrivers]
                        .find(item => item.kierowca_rfid === driver.kierowca_rfid || item.kierowca_nazwa === driver.kierowca_nazwa));

                if (notValidNameOrRFID)
                    return duplicatedNameOrRFID.push(driver);
                if (!isEmail(driver.kierowca_email) && driver.kierowca_email !== '')
                    return withoutValidEmail.push(driver);
                if (!isPhoneNumber(driver.kierowca_telefon) && driver.kierowca_telefon !== '')
                    return withoutValidPhone.push(driver);
                newDrivers.push(driver);
            } else {
                if (Object.values(driver).find(x => x === '')) incompleteData.push(driver);
            }
        });
        if (newDrivers.length > 0)
            props.addMultipleDrivers(newDrivers);
        if (!notArrayOrEmpty(incompleteData) || !notArrayOrEmpty(duplicatedNameOrRFID) || !notArrayOrEmpty(withoutValidEmail) || !notArrayOrEmpty(withoutValidPhone))
            return setNotificationDialog({
                incompleteRecords: incompleteData,
                duplicatedNameOrRFID: duplicatedNameOrRFID,
                withoutValidEmail: withoutValidEmail,
                withoutValidPhone: withoutValidPhone
            });
        window.location.reload();
    };

    const handleDriverDelete = id => {
        props.deleteDriver(id);
        handleDialogClose();
    };

    const handleDriverDetails = id => {
        setDriverIdToEdit(id);
        setOpen(true);
    };

    const handleEcoGradeClick = id => {
        props.getDriverEcoChart(id);
        setDriverIdToEdit(id);
        setChartOpen(true);
    };

    const handleChartClose = () => {
        props.cleanDriverChartData();
        setChartOpen(false);
        setDriverIdToEdit(null);
    };

    const handleDialogClose = () => {
        setDriverIdToEdit(null);
        setOpen(false);
    };

    const handleCloseNotificationDialog = () => {
        setNotificationDialog(null);
        window.location.reload();
    };

    const handleUrlClear = () => {
        props.history.push({
            pathname: "/drivers",
        });
        setSearchDriver(null);
    };

    return (
        <PaperContainer>
            <div className={clsx(classes.row, {[classes.hide]: nonAdminUser})}>
                <SearchField
                    className={classes.autocomplete}
                    options={drivers.map(driver => driver.kierowca_nazwa)}
                    onChange={(value) => setSearchDriver(value)}
                    onClearClick={handleUrlClear}
                />
                <ImportOrExportFileWithTemplate
                    tooltipText={t.importCsvDescriptionDriverAdd}
                    template={AddDriversTemplate}
                    exportFilename={t.driversExportFilename}
                    exportCsvHeaders={csvHeaders}
                    exportData={drivers}
                    onFileLoaded={handleCsvLoaded}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpen(true)}
                >
                    {t.addDriver}
                </Button>
            </div>
            <DriversTable
                height={countHeightWhen2HeightToolbarUsed(height ? height : null)}
                drivers={drivers}
                onDetailsClick={handleDriverDetails}
                onEcoGradeClick={handleEcoGradeClick}
                searchedDriver={searchDriver}
            />
            <AddDriver
                open={open}
                onClose={handleDialogClose}
                isEdit={driverIdToEdit !== null}
                data={drivers.find(driver => driver.kierowca_id === driverIdToEdit)}
                groups={groups}
                onSubmit={handleDriverSubmit}
                onDelete={handleDriverDelete}
                onMobileAppKeyGenerate={(id) => props.generateMobileKey(id)}
            />
            <NotificationDialog
                data={notificationDialog}
                onClose={handleCloseNotificationDialog}
            />
            <EcoChartDialog
                data={chartData}
                open={chartOpen}
                onClose={handleChartClose}
                isDriver
                driverData={drivers.find(driver => driver.kierowca_id === driverIdToEdit)}
            />
        </PaperContainer>
    );
};

const mapStateToProps = (state) => ({
    groups: state.filterReducer.groupOptions,
    drivers: state.driverReducer.availableDrivers,
    chartData: state.driverReducer.ecoChart
});

const mapDispatchToProps = dispatch => {
    return bindActionCreators(ActionCreator, dispatch);
};


DriversView.propTypes = {
    classes: PropTypes.object,
    //redux
    groups: PropTypes.array,
    drivers: PropTypes.array,
    chartData: PropTypes.array,
    getDrivers: PropTypes.func,
    patchDriver: PropTypes.func,
    deleteDriver: PropTypes.func,
    addDriver: PropTypes.func,
    rfidNotValid: PropTypes.func,
    dataNotValid: PropTypes.func,
    getDriverEcoChart: PropTypes.func,
    cleanDriverChartData: PropTypes.func,
    generateMobileKey: PropTypes.func,
    addMultipleDrivers: PropTypes.func,
    emailNotValid: PropTypes.func,
    //withSubordinateUser
    nonAdminUser: PropTypes.bool,
    //withRouter
    history: PropTypes.object
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    withStyles(useStyles),
    withSubordinateUser,
    withRouter
)(DriversView);